import type { ComponentType, SVGProps } from 'react';
import { cn } from '@/lib/cn';

interface StatTileProps {
  label: string;
  value: string | number;
  /** Optional icon chip. Omit when the surrounding card header already shows one. */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Optional secondary line (e.g. "12 active"). */
  sublabel?: string;
  /** Optional trend delta, positive/negative colored. */
  delta?: { value: string; positive?: boolean };
  accent?: 'brand' | 'success' | 'warning' | 'danger' | 'info';
}

const accentBg: Record<NonNullable<StatTileProps['accent']>, string> = {
  brand: 'bg-brand/10 text-brand',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  danger: 'bg-danger/10 text-danger',
  info: 'bg-info/10 text-info',
};

/** Compact KPI tile: icon chip + big value + label + optional sublabel/delta. */
export function StatTile({ label, value, icon: Icon, sublabel, delta, accent = 'brand' }: StatTileProps) {
  return (
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-fg-muted">{label}</p>
        <p className="mt-1 text-3xl font-semibold tracking-tight text-fg">{value}</p>
        <div className="mt-1 flex items-center gap-2">
          {sublabel && <span className="text-xs text-fg-subtle">{sublabel}</span>}
          {delta && (
            <span className={cn('text-xs font-medium', delta.positive ? 'text-success' : 'text-danger')}>
              {delta.positive ? '↑' : '↓'} {delta.value}
            </span>
          )}
        </div>
      </div>
      {Icon && (
        <span className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-lg', accentBg[accent])}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      )}
    </div>
  );
}
